import { types } from "./mutations";
import Feature, {
  CASCADABLE_PROPERTIES,
  FEATURE_TYPES,
} from "@/models/Feature.model.js";
import FileSaver from "file-saver";
// import debounce from "lodash/debounce";
import omit from "lodash/omit";
const exportWorker = new Worker(new URL("../../../workers/export.worker.js", import.meta.url));
const fileLoaderWorker = new Worker(new URL("../../../workers/fileLoader.worker.js", import.meta.url));
const geojsonWorker = new Worker(new URL("../../../workers/geojson.worker.js", import.meta.url));
import { wrap } from "comlink";
import tokml from "tokml";
import toGeoJSON from "@mapbox/togeojson";
import { PROJECT_ACTIONS } from "@/utils/constants";
import { generateEditorScreenshot } from "@/utils/Screenshot";
import { fileReader } from "@/utils/FileUtils";
const workerFunctions = wrap(fileLoaderWorker);
const geojsonFunctions = wrap(geojsonWorker);
const exportFunctions = wrap(exportWorker);

const highlightTimeouts = new Map();
const clearHighlightTimeout = (featureId) => {
  const timeout = highlightTimeouts.get(featureId);
  if (timeout) {
    clearTimeout(timeout);
    highlightTimeouts.delete(featureId);
  }
};

const getLastItemIndex = (features) => {
  const indexes = {
    Polygon: 0,
    MultiPolygon: 0,
    LineString: 0,
    Point: 0,
    MultiLineString: 0
  };

  const getIndexFromFeatureName = (feature) => {
    const featureType = feature.geometry.type;
    const featureName = feature.properties.name;
    const regex = new RegExp(`${featureType} ([0-9]+)`);
    if (regex.test(featureName)) {
      const index = parseInt(featureName.match(regex)[1]);
      return index;
    } else {
      return null;
    }
  };

  features.forEach((feature) => {
    let currentIndex = indexes[feature.geometry.type];
    const featureIndex = getIndexFromFeatureName(feature);
    if (featureIndex && featureIndex > currentIndex) {
      indexes[feature.geometry.type] = featureIndex;
    }
  });

  return indexes;
};

const getNextObjectId = (features) => {
  const keys = ["OBJECTID", "OBJECT_ID", "object_id", "objectid", "ObjectId"];
  let maxId = 0;
  Object.values(features || {}).forEach((feature) => {
    const props = feature?.properties || {};
    keys.forEach((key) => {
      const value = Number(props[key]);
      if (Number.isFinite(value) && value > maxId) {
        maxId = value;
      }
    });
  });
  return maxId + 1;
};

const ensureLineObjectIds = (featuresMap, featureIds, protectedIds = []) => {
  const keys = ["OBJECTID", "OBJECT_ID", "object_id", "objectid", "ObjectId"];
  const protectedSet = new Set(protectedIds);
  const lineIds = featureIds.filter((id) => {
    const type = featuresMap?.[id]?.geometry?.type;
    return type === FEATURE_TYPES.LineString || type === FEATURE_TYPES.MultiLineString;
  });
  let maxId = 0;
  const updatedIds = new Set();
  const getIdMeta = (props) => {
    const key = keys.find((k) => Number.isFinite(Number(props?.[k])));
    return key ? { key, value: Number(props[key]) } : null;
  };

  lineIds.forEach((id) => {
    const props = featuresMap[id]?.properties || {};
    keys.forEach((key) => {
      const value = Number(props[key]);
      if (Number.isFinite(value) && value > maxId) {
        maxId = value;
      }
    });
  });
  const usedIds = new Set();
  lineIds.forEach((id) => {
    const props = featuresMap[id]?.properties || {};
    keys.forEach((key) => {
      const value = Number(props[key]);
      if (Number.isFinite(value)) {
        usedIds.add(value);
      }
    });
  });
  const nextId = () => {
    maxId += 1;
    while (usedIds.has(maxId)) {
      maxId += 1;
    }
    usedIds.add(maxId);
    return maxId;
  };
  const updated = { ...featuresMap };
  const assignNewId = (id) => {
    const feature = updated[id];
    if (!feature?.properties) return;
    const props = { ...feature.properties, OBJECTID: nextId() };
    keys.forEach((key) => {
      if (key !== "OBJECTID") {
        delete props[key];
      }
    });
    updated[id] = new Feature({
      ...feature,
      properties: props,
    });
    updatedIds.add(id);
  };

  lineIds.forEach((id) => {
    const meta = getIdMeta(updated[id]?.properties || {});
    if (!meta) {
      assignNewId(id);
    }
  });

  const idMap = new Map();
  lineIds.forEach((id) => {
    const meta = getIdMeta(updated[id]?.properties || {});
    if (!meta) return;
    if (!idMap.has(meta.value)) {
      idMap.set(meta.value, []);
    }
    idMap.get(meta.value).push({ id, key: meta.key });
  });

  const protectedCount = (entries) =>
    entries.reduce(
      (count, entry) => count + (protectedSet.has(entry.id) ? 1 : 0),
      0
    );

  idMap.forEach((entries) => {
    if (entries.length < 2) return;
    const protectedTotal = protectedCount(entries);
    if (protectedTotal > 0) {
      entries.forEach((entry) => {
        if (!protectedSet.has(entry.id)) {
          assignNewId(entry.id);
        }
      });
      return;
    }
    entries.forEach((entry, index) => {
      if (index > 0) {
        assignNewId(entry.id);
      }
    });
  });

  return { features: updated, updatedIds: Array.from(updatedIds) };
};

const cloneSnapshot = (state) => ({
  features: JSON.parse(JSON.stringify(state.features || {})),
  featureIds: JSON.parse(JSON.stringify(state.featureIds || [])),
});

const pushUndoSnapshot = (commit, state) => {
  const nextUndoStack = [...(state.undoStack || []), cloneSnapshot(state)];
  commit(types.SET_UNDO_STACK, nextUndoStack);
  commit(types.SET_REDO_STACK, []);
};

// const syncThumbnail = (dispatch) => {
//   dispatch("projects/syncProjectThumbnail", null, { root: true });
// };

// const debouncedSyncThumbnail = debounce(syncThumbnail, 3000);

const sync = (dispatch, action, payload) => {
  dispatch("projects/sync", { action, payload }, { root: true });
  // debouncedSyncThumbnail(dispatch);
};

const isLineFeature = (type) =>
  type === FEATURE_TYPES.LineString || type === FEATURE_TYPES.MultiLineString;
const isPointFeature = (type) =>
  type === FEATURE_TYPES.Point || type === "MultiPoint";

const getPointCategory = (feature) => {
  const props = feature?.properties || {};
  const raw = [
    props.FEATURETYPE,
    props.TYPE,
    props.Type,
    props.SUBTYPE,
    props.CLASS,
    props.CATEGORY,
  ]
    .filter(Boolean)
    .map((value) => `${value}`.toLowerCase())
    .join(" ");
  if (raw.includes("substation") || raw.includes("transformer")) return "substation";
  if (raw.includes("generator") || raw.includes("power") || raw.includes("plant")) {
    return "generator";
  }
  return "other";
};

const getFeatureName = (feature) =>
  feature?.properties?.NAME || feature?.properties?.name || "";

const getRevisedTimestamp = () => new Date().toISOString();

const POINT_EPSILON = 1e-8;
const ANGLE_TOLERANCE_DEG = 5;

const pointsEqual = (a, b, eps = POINT_EPSILON) => {
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;
};

const normalizeLineString = (coords, angleToleranceDeg = ANGLE_TOLERANCE_DEG) => {
  if (!Array.isArray(coords)) return coords;
  const cleaned = [];
  for (let i = 0; i < coords.length; i += 1) {
    const point = coords[i];
    if (!cleaned.length || !pointsEqual(cleaned[cleaned.length - 1], point)) {
      cleaned.push(point);
    }
  }
  if (cleaned.length < 3) return cleaned;
  const tolRad = (Math.PI / 180) * angleToleranceDeg;
  const output = [cleaned[0]];
  for (let i = 1; i < cleaned.length - 1; i += 1) {
    const prev = output[output.length - 1];
    const curr = cleaned[i];
    const next = cleaned[i + 1];
    const v1x = curr[0] - prev[0];
    const v1y = curr[1] - prev[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 <= POINT_EPSILON || len2 <= POINT_EPSILON) {
      continue;
    }
    const dot = v1x * v2x + v1y * v2y;
    const cos = Math.min(1, Math.max(-1, dot / (len1 * len2)));
    const angle = Math.acos(cos);
    const isColinear = angle <= tolRad || Math.abs(Math.PI - angle) <= tolRad;
    if (!isColinear) {
      output.push(curr);
    }
  }
  output.push(cleaned[cleaned.length - 1]);
  return output;
};

const stitchMultiLine = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  const remaining = lines.map((line) => [...line]);
  let chain = remaining.shift();
  let changed = true;
  while (changed && remaining.length) {
    changed = false;
    for (let i = 0; i < remaining.length; i += 1) {
      const candidate = remaining[i];
      const candStart = candidate[0];
      const candEnd = candidate[candidate.length - 1];
      const chainStart = chain[0];
      const chainEnd = chain[chain.length - 1];
      if (pointsEqual(candStart, chainEnd)) {
        chain = chain.concat(candidate.slice(1));
        remaining.splice(i, 1);
        changed = true;
        break;
      }
      if (pointsEqual(candEnd, chainEnd)) {
        chain = chain.concat(candidate.slice(0, -1).reverse());
        remaining.splice(i, 1);
        changed = true;
        break;
      }
      if (pointsEqual(candEnd, chainStart)) {
        chain = candidate.slice(0, -1).concat(chain);
        remaining.splice(i, 1);
        changed = true;
        break;
      }
      if (pointsEqual(candStart, chainStart)) {
        chain = candidate.slice(1).reverse().concat(chain);
        remaining.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  if (remaining.length) return null;
  return chain;
};

const normalizeLineGeometry = (geometry) => {
  if (!geometry || !geometry.type) return geometry;
  if (geometry.type === FEATURE_TYPES.LineString) {
    return {
      ...geometry,
      coordinates: normalizeLineString(geometry.coordinates || []),
    };
  }
  if (geometry.type === FEATURE_TYPES.MultiLineString) {
    const lines = (geometry.coordinates || []).map((line) =>
      normalizeLineString(line || [])
    );
    const stitched = stitchMultiLine(lines);
    if (stitched && stitched.length >= 2) {
      return {
        type: FEATURE_TYPES.LineString,
        coordinates: normalizeLineString(stitched),
      };
    }
    return {
      ...geometry,
      coordinates: lines,
    };
  }
  return geometry;
};

const buildFilteredGeoJson = (geoJson, filterFn) => {
  const clone = JSON.parse(JSON.stringify({ ...geoJson }));
  clone.features = (clone.features || []).filter(filterFn);
  return clone;
};

const buildUnfilteredGeoJson = (state, filterFn) => {
  const features = [...(state.featureIds || [])]
    .map((id) => state.features[id])
    .filter(Boolean)
    .filter(filterFn);
  return {
    type: "FeatureCollection",
    features: JSON.parse(JSON.stringify(features)),
  };
};

const LINE_EXPORT_PROPERTIES = [
  "OBJECTID",
  "FEATURETYPE",
  "DESCRIPTION",
  "CREATED_BY",
  "CLASS",
  "SPLIT_CATEGORY",
  "NAME",
  "OPERATIONALSTATUS",
  "CAPACITYKV",
  "STATE",
  "SPATIALCONFIDENCE",
  "REVISED",
  "COMMENT",
  "GA_GUID",
  "SHAPE_Length",
  "LENGTH_M",
];

const getObjectIdValue = (props) => {
  const keys = ["OBJECTID", "OBJECT_ID", "object_id", "objectid", "ObjectId"];
  for (const key of keys) {
    const value = Number(props?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const limitLineExportProperties = (features) =>
  (features || []).map((feature) => {
    const props = feature?.properties || {};
    const limited = LINE_EXPORT_PROPERTIES.reduce((result, key) => {
      if (Object.prototype.hasOwnProperty.call(props, key)) {
        result[key] = props[key];
        return result;
      }
      if (key === "OBJECTID") {
        const objectId = getObjectIdValue(props);
        if (objectId !== null) {
          result.OBJECTID = objectId;
        }
      }
      if (key === "NAME" && props.name) {
        result.NAME = props.name;
      }
      return result;
    }, {});
    return {
      type: feature?.type,
      geometry: feature?.geometry,
      properties: limited,
    };
  });

export default {
  setFilename: ({ commit }, filename) => {
    commit(types.SET_FILENAME, filename);
  },
  syncFileName: ({ state, dispatch }) => {
    const filename = state.filename;
    sync(dispatch, PROJECT_ACTIONS.UPDATE_TITLE, filename);
  },
  setGeoJson: async ({ commit }, geoJson) => {
    commit(types.SET_GEO_JSON, geoJson);
  },
  updateBoundingBox: async ({ commit, getters }, { bbox } = { bbox: null }) => {
    try {
      if (bbox) {
        commit(types.SET_BOUNDING_BOX, bbox);
        return;
      }
      const boundingBox = await geojsonFunctions.getGeoJsonBoundingBox(
        getters.getGeoJson
      );
      commit(types.SET_BOUNDING_BOX, boundingBox);
    } catch (error) {
      console.error(error);
    }
  },
  addGeoJsonFeatures: async (
    { state, commit, dispatch },
    { features, shouldUpdateBBOX, cascadeStyles, source } = { features: [], cascadeStyles: true, shouldUpdateBBOX: false, source: "local" }
  ) => {
    try {
      if (features?.length) {
        pushUndoSnapshot(commit, state);
      }
      const lastItemIndex = getLastItemIndex(Object.values(state.features));
      const featuresToAdd = features.reduce((result, feature, index) => {
        const initialProperties = { ...(feature.properties || {}) };
        if (initialProperties.name || initialProperties.NAME) {
          const normalizedName = initialProperties.name ?? initialProperties.NAME;
          initialProperties.name = normalizedName;
          initialProperties.NAME = normalizedName;
        }
        const baseProperties = {
          ...initialProperties,
          ...((cascadeStyles && state.shouldCascadeFeatureProperties &&
            state.cascadeFeatureProperties[feature.geometry.type]) ||
            {}),
          ...(initialProperties.name?.length
            ? {}
            : {
                name: `${feature.geometry.type} ${
                  lastItemIndex[feature.geometry.type] + index + 1
                }`,
              }),
        };
        if (
          isLineFeature(feature?.geometry?.type) &&
          !baseProperties.REVISED
        ) {
          baseProperties.REVISED = getRevisedTimestamp();
        }
        if (baseProperties.name && !baseProperties.NAME) {
          baseProperties.NAME = baseProperties.name;
        }
        const item = new Feature({
          ...feature,
          properties: baseProperties,
        });
        return {
          ...result,
          [item.properties.id]: { ...item },
        };
      }, {});
      const addedFeatureIds = Object.keys(featuresToAdd);
      let newFeatures = { ...state.features, ...featuresToAdd };
      const protectedIds =
        source === "import" ? addedFeatureIds : state.featureIds || [];
      const ensureResult = ensureLineObjectIds(
        newFeatures,
        [...Object.keys(newFeatures)],
        protectedIds
      );
      newFeatures = ensureResult.features;
      const newFeatureIds = [
        ...[...addedFeatureIds].reverse(),
        ...state.featureIds,
      ];

      commit(types.SET_FEATURES, {
        features: newFeatures,
        featureIds: newFeatureIds,
      });
      setTimeout(() => {
        commit(types.SET_SELECTED_FEATURE_IDS, addedFeatureIds);
      }, 100);

      if (shouldUpdateBBOX) {
        dispatch("updateBoundingBox");
      }
      // sync with remote server
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, featuresToAdd);
      if (ensureResult.updatedIds?.length) {
        const updated = ensureResult.updatedIds.reduce((result, id) => {
          result[id] = newFeatures[id];
          return result;
        }, {});
        sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, updated);
      }
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, newFeatureIds);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  updateFeatureProperties: async (
    { state, commit, dispatch },
    { featureId, properties, geometry, replaceProperties, shouldSync } = {
      featureId: null,
      properties: {},
      geometry: {},
      replaceProperties: false,
      shouldSync: true,
    }
  ) => {
    const existingFeature = state.features[featureId];
    pushUndoSnapshot(commit, state);
    const nextProperties = {
      ...(replaceProperties ? {} : existingFeature?.properties),
      ...properties,
    };
    const mergedGeometry = {
      ...existingFeature?.geometry,
      ...geometry,
    };
    const normalizedGeometry =
      mergedGeometry &&
      isLineFeature(mergedGeometry.type) &&
      mergedGeometry.coordinates
        ? normalizeLineGeometry(mergedGeometry)
        : mergedGeometry;
    if (
      existingFeature?.geometry?.type &&
      isLineFeature(existingFeature.geometry.type) &&
      ((properties && Object.keys(properties).length) ||
        (geometry && Object.keys(geometry).length))
    ) {
      nextProperties.REVISED = getRevisedTimestamp();
    }
    if (nextProperties.name || nextProperties.NAME) {
      const normalizedName = nextProperties.name ?? nextProperties.NAME;
      nextProperties.name = normalizedName;
      nextProperties.NAME = normalizedName;
    }
    const updatedFeature = new Feature({
      ...existingFeature,
      geometry: normalizedGeometry,
      properties: nextProperties,
    });
    commit(types.SET_FEATURES, {
      features: {
        ...state.features,
        [featureId]: updatedFeature,
      },
      featureIds: state.featureIds,
    });
    if (shouldSync) {
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, {
        [featureId]: updatedFeature,
      });
    }

    // add to cascadable properties
    if (properties && Object.keys(properties).length) {
      const cascadeFeatureProperties = Object.keys(properties).reduce(
        (result, property) => {
          if (CASCADABLE_PROPERTIES.indexOf(property) > -1) {
            return {
              ...result,
              [property]: properties[property],
            };
          } else {
            return result;
          }
        },
        {}
      );
      dispatch("setCascadeFeatureProperties", {
        featureType: updatedFeature.geometry.type,
        properties: cascadeFeatureProperties,
      });
    }
  },
  updateMultipleFeatureProperties: async (
    { state, commit, dispatch },
    { featureIds, properties, shouldSync } = {
      featureIds: [],
      properties: {},
      shouldSync: false,
    }
  ) => {
    if (featureIds?.length) {
      pushUndoSnapshot(commit, state);
    }
    const updatedFeatures = featureIds.reduce((result, featureId) => {
      const existingFeature = state.features[featureId];
      const nextProperties = {
        ...existingFeature?.properties,
        ...properties,
      };
      if (
        existingFeature?.geometry?.type &&
        isLineFeature(existingFeature.geometry.type) &&
        properties &&
        Object.keys(properties).length
      ) {
        nextProperties.REVISED = getRevisedTimestamp();
      }
      if (nextProperties.name || nextProperties.NAME) {
        const normalizedName = nextProperties.name ?? nextProperties.NAME;
        nextProperties.name = normalizedName;
        nextProperties.NAME = normalizedName;
      }
      const updatedFeature = new Feature({
        ...existingFeature,
        properties: nextProperties,
      });
      return {
        ...result,
        [featureId]: updatedFeature,
      };
    }, {});
    commit(types.SET_FEATURES, {
      features: {
        ...state.features,
        ...updatedFeatures,
      },
      featureIds: state.featureIds,
    });
    if (shouldSync) {
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, updatedFeatures);
    }
  },
  syncFeatureProperties: async (
    { dispatch, state },
    { featureId, featureIds }
  ) => {
    if (featureIds?.length) {
      const features = featureIds.reduce((result, featureId) => {
        return {
          ...result,
          [featureId]: state.features[featureId],
        };
      }, {});
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, features);
    } else if (featureId) {
      sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, {
        [featureId]: state.features[featureId],
      });
    }
  },
  updateFeaturePosition: async (
    { state, commit, dispatch },
    { fromIndex, toIndex }
  ) => {
    const featureIdsCopy = [...state.featureIds];
    const fi = featureIdsCopy.length - fromIndex - 1;
    const ti = featureIdsCopy.length - toIndex - 1;
    const element = featureIdsCopy[fi];
    featureIdsCopy.splice(fi, 1);
    featureIdsCopy.splice(ti, 0, element);
    commit(types.SET_FEATURE_ID_LIST, featureIdsCopy);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, featureIdsCopy);
  },
  setFeatures: async ({ commit }, { features, featureIds }) => {
    commit(types.SET_FEATURES, {
      features,
      featureIds,
    });
  },
  setFeatureIdList: async ({ commit }, { featureIds }) => {
    commit(types.SET_FEATURE_ID_LIST, featureIds);
  },
  setImportLocked: async ({ commit }, flag) => {
    commit(types.SET_IMPORT_LOCKED, flag);
  },
  deleteSelectedFeatures: async ({ state, commit, dispatch }) => {
    if (state.selectedFeatureIds?.length) {
      pushUndoSnapshot(commit, state);
    }
    const featuresToDelete = [...state.selectedFeatureIds];
    await dispatch("setSelectedFeatureIds", []);
    await dispatch("setHoveredFeatureId", null);
    if (featuresToDelete.length) {
      const pinned = new Set(state.pinnedFeatureIds || []);
      featuresToDelete.forEach((id) => pinned.delete(id));
      commit(types.SET_PINNED_FEATURE_IDS, Array.from(pinned));
    }
    const featureIds = state.featureIds.filter(
      (id) => featuresToDelete.indexOf(id) === -1
    );
    commit(types.SET_FEATURES, {
      features: omit(state.features, featuresToDelete),
      featureIds,
    });
    sync(dispatch, PROJECT_ACTIONS.DELETE_FEATURES, featuresToDelete);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, featureIds);
  },
  deleteFeaturesBulk: async ({ state, commit, dispatch }, { featureIds }) => {
    const idsToDelete = (featureIds || []).filter(
      (id) => !!state.features?.[id]
    );
    if (!idsToDelete.length) return;
    pushUndoSnapshot(commit, state);
    const pinned = new Set(state.pinnedFeatureIds || []);
    idsToDelete.forEach((id) => pinned.delete(id));
    commit(types.SET_PINNED_FEATURE_IDS, Array.from(pinned));
    const remainingIds = state.featureIds.filter(
      (id) => idsToDelete.indexOf(id) === -1
    );
    commit(types.SET_FEATURES, {
      features: omit(state.features, idsToDelete),
      featureIds: remainingIds,
    });
    if (state.selectedFeatureIds?.length) {
      const remainingSelected = state.selectedFeatureIds.filter(
        (id) => idsToDelete.indexOf(id) === -1
      );
      commit(types.SET_SELECTED_FEATURE_IDS, remainingSelected);
    }
    if (idsToDelete.includes(state.hoveredFeatureId)) {
      commit(types.SET_HOVERED_FEATURE_ID, null);
    }
    sync(dispatch, PROJECT_ACTIONS.DELETE_FEATURES, idsToDelete);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, remainingIds);
  },
  deleteFeatureById: async ({ state, commit, dispatch }, { featureId }) => {
    if (featureId && state.features?.[featureId]) {
      pushUndoSnapshot(commit, state);
    }
    // clean up existing feature from records
    if (
      state.selselectedFeatureIds &&
      state.selselectedFeatureIds.indexOf(featureId) > -1
    ) {
      await dispatch(
        "setSelectedFeatureIds",
        state.selselectedFeatureIds.filter((id) => id !== featureId)
      );
    }
    if (state.hoveredFeatureId === featureId) {
      await dispatch("setHoveredFeatureId", null);
    }

    const featureIds = state.featureIds.filter((id) => id !== featureId);
    if (featureId) {
      const pinned = new Set(state.pinnedFeatureIds || []);
      pinned.delete(featureId);
      commit(types.SET_PINNED_FEATURE_IDS, Array.from(pinned));
    }
    commit(types.SET_FEATURES, {
      features: omit(state.features, [featureId]),
      featureIds,
    });

    sync(dispatch, PROJECT_ACTIONS.DELETE_FEATURES, [featureId]);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, featureIds);
  },
  setActiveTool: async ({ commit }, tool) => {
    commit(types.SET_ACTIVE_TOOL, tool);
  },
  setActiveEditMode: async ({ commit }, { mode, featureId }) => {
    commit(types.SET_SELECTED_FEATURE_IDS, featureId ? [featureId] : []);
    commit(types.SET_ACTIVE_EDIT_MODE, mode);
  },
  setHoveredFeatureId: async ({ commit }, featureId) => {
    commit(types.SET_HOVERED_FEATURE_ID, featureId);
  },
  setSelectedFeatureIds: async ({ commit }, featureIds) => {
    commit(types.SET_SELECTED_FEATURE_IDS, featureIds);
  },
  selectAllFeatures: async ({ state, dispatch }) => {
    await dispatch("setSelectedFeatureIds", state.featureIds);
  },
  customSelectFeatureId: async (
    { commit, state },
    { featureId, deselect, isMultiSelect } = {
      featureId: null,
      deselect: false,
      isMultiSelect: false,
    }
  ) => {
    const featureIds = [...state.featureIds].reverse();
    if (!featureId) return;
    if (deselect) {
      commit(
        types.SET_SELECTED_FEATURE_IDS,
        state.selectedFeatureIds.filter((id) => id !== featureId)
      );
    } else if (isMultiSelect) {
      if (state.selectedFeatureIds.length > 0) {
        let lastItemIndex = state.selectedFeatureIds.reduce((result, item) => {
          const currentItemIndex = featureIds.indexOf(item);
          if (currentItemIndex > result) {
            return currentItemIndex;
          } else {
            return result;
          }
        }, -1);
        if (lastItemIndex > -1) {
          let featureIdIndex = featureIds.indexOf(featureId);
          if (featureIdIndex > lastItemIndex) {
            featureIdIndex += 1;
          }
          const minIndex = Math.min(lastItemIndex, featureIdIndex);
          const maxIndex = Math.max(lastItemIndex, featureIdIndex);
          const selectedIds = featureIds.slice(minIndex, maxIndex);
          commit(types.SET_SELECTED_FEATURE_IDS, [
            ...new Set([...state.selectedFeatureIds, ...selectedIds]),
          ]);
        }
      }
    } else {
      if (state.selectedFeatureIds.indexOf(featureId) > -1) {
        commit(
          types.SET_SELECTED_FEATURE_IDS,
          state.selectedFeatureIds.filter((id) => id !== featureId)
        );
      } else {
        commit(types.SET_SELECTED_FEATURE_IDS, [
          ...state.selectedFeatureIds,
          featureId,
        ]);
      }
    }
  },
  setCascadeFeatureProperties: async (
    { state, commit },
    { featureType, properties }
  ) => {
    commit(
      types.SET_CASCADE_FEATURE_PROPERTIES,
      Object.freeze({
        ...state.cascadeFeatureProperties,
        [featureType]: {
          ...(state.cascadeFeatureProperties[featureType] || {}),
          ...properties,
        },
      })
    );
  },
  setFeatureFilters: async ({ state, commit }, updates = {}) => {
    commit(types.SET_FEATURE_FILTERS, Object.freeze({
      ...state.featureFilters,
      ...updates,
    }));
  },
  togglePinnedFeature: async ({ state, commit }, { featureId }) => {
    if (!featureId) return;
    const current = new Set(state.pinnedFeatureIds || []);
    if (current.has(featureId)) {
      current.delete(featureId);
    } else {
      current.add(featureId);
    }
    commit(types.SET_PINNED_FEATURE_IDS, Array.from(current));
  },
  setHighlightedFeatureIds: async ({ commit }, featureIds) => {
    commit(types.SET_HIGHLIGHTED_FEATURE_IDS, featureIds || []);
  },
  clearHighlightedFeatureIds: async ({ state, commit }) => {
    (state.highlightedFeatureIds || []).forEach((featureId) =>
      clearHighlightTimeout(featureId)
    );
    commit(types.SET_HIGHLIGHTED_FEATURE_IDS, []);
  },
  flashHighlightFeatureIds: async (
    { state, commit },
    { featureIds = [], durationMs = 2200 } = {}
  ) => {
    const ids = (featureIds || []).filter(Boolean);
    if (!ids.length) return;

    const duration = Math.max(0, Number(durationMs) || 0);
    const next = new Set(state.highlightedFeatureIds || []);
    ids.forEach((featureId) => {
      next.add(featureId);
      clearHighlightTimeout(featureId);
      highlightTimeouts.set(
        featureId,
        setTimeout(() => {
          const current = new Set(state.highlightedFeatureIds || []);
          if (!current.has(featureId)) return;
          current.delete(featureId);
          commit(types.SET_HIGHLIGHTED_FEATURE_IDS, Array.from(current));
          highlightTimeouts.delete(featureId);
        }, duration)
      );
    });
    commit(types.SET_HIGHLIGHTED_FEATURE_IDS, Array.from(next));
  },
  duplicateLineFeature: async (
    { state, dispatch },
    { featureId, offsetMeters = 50 } = {}
  ) => {
    const source = state.features[featureId];
    if (!source || !source.geometry) return false;
    const geometryType = source.geometry.type;
    if (geometryType !== "LineString" && geometryType !== "MultiLineString") {
      return false;
    }

    const latOffset = -offsetMeters / 111320;
    const offsetCoord = (coord) => {
      const [lng, lat, ...rest] = coord;
      return [lng, lat + latOffset, ...rest];
    };

    const originalCoords = JSON.parse(
      JSON.stringify(source.geometry.coordinates || [])
    );
    if (!originalCoords.length) return false;
    const offsetLines =
      geometryType === "LineString"
        ? [originalCoords.map(offsetCoord)]
        : originalCoords.map((line) => line.map(offsetCoord));

    const originalStart =
      geometryType === "LineString"
        ? originalCoords[0]
        : originalCoords[0][0];
    const originalEnd =
      geometryType === "LineString"
        ? originalCoords[originalCoords.length - 1]
        : originalCoords[originalCoords.length - 1][
            originalCoords[originalCoords.length - 1].length - 1
          ];
    const offsetStart = offsetLines[0]?.[0];
    const offsetEnd =
      offsetLines[offsetLines.length - 1]?.[
        offsetLines[offsetLines.length - 1].length - 1
      ];
    if (!originalStart || !originalEnd || !offsetStart || !offsetEnd) {
      return false;
    }

    const duplicateGeometry = {
      type: "MultiLineString",
      coordinates: [
        [originalStart, offsetStart],
        ...offsetLines,
        [originalEnd, offsetEnd],
      ],
    };

    const originalName = getFeatureName(source) || "Line";
    const duplicateName = `${originalName}_2`;
    const hasDuplicateName = Object.values(state.features || {}).some(
      (feature) => getFeatureName(feature) === duplicateName
    );
    if (hasDuplicateName) {
      return false;
    }
    const clonedProperties = JSON.parse(
      JSON.stringify(source.properties || {})
    );
    delete clonedProperties.id;
    const nextObjectId = getNextObjectId(state.features);
    if (Number.isFinite(nextObjectId)) {
      if ("OBJECTID" in clonedProperties) {
        clonedProperties.OBJECTID = nextObjectId;
      } else if ("OBJECT_ID" in clonedProperties) {
        clonedProperties.OBJECT_ID = nextObjectId;
      } else if ("object_id" in clonedProperties) {
        clonedProperties.object_id = nextObjectId;
      } else if ("objectid" in clonedProperties) {
        clonedProperties.objectid = nextObjectId;
      } else if ("ObjectId" in clonedProperties) {
        clonedProperties.ObjectId = nextObjectId;
      } else {
        clonedProperties.OBJECTID = nextObjectId;
      }
    }
    const newProperties = {
      ...clonedProperties,
      name: duplicateName,
      NAME: duplicateName,
    };
    
    const feature = {
      type: "Feature",
      geometry: duplicateGeometry,
      properties: newProperties,
    };

    await dispatch("addGeoJsonFeatures", {
      features: [feature],
      cascadeStyles: false,
      shouldUpdateBBOX: false,
    });
    return true;
  },
  setFocusedFeatureId: async ({ commit }, featureId) => {
    commit(types.SET_FOCUSED_FEATURE_ID, featureId);
  },
  saveAsGeoJson: async ({ getters, state, commit }, { applyFilters = true } = {}) => {
    const geoJsonToExport = applyFilters
      ? JSON.parse(JSON.stringify({ ...getters.getGeoJson }))
      : buildUnfilteredGeoJson(state, () => true);
    geoJsonToExport.features = (geoJsonToExport.features || []).map((feature) => {
      if (isLineFeature(feature?.geometry?.type)) {
        return limitLineExportProperties([feature])[0];
      }
      return {
        type: feature?.type,
        geometry: feature?.geometry,
        properties: feature?.properties || {},
      };
    });
    const blob = new Blob([JSON.stringify(geoJsonToExport, null, 2)], {
      type: "application/json",
    });
    FileSaver.saveAs(blob, "mapster.json");
    commit(types.SET_UNDO_STACK, []);
    commit(types.SET_REDO_STACK, []);
  },
  saveAsGeoJsonLines: async ({ getters, state, commit }, { applyFilters = true } = {}) => {
    const geoJsonToExport = applyFilters
      ? buildFilteredGeoJson(getters.getGeoJson, (feature) =>
          isLineFeature(feature?.geometry?.type)
        )
      : buildUnfilteredGeoJson(state, (feature) =>
          isLineFeature(feature?.geometry?.type)
        );
    geoJsonToExport.features = limitLineExportProperties(
      geoJsonToExport.features
    );
    const blob = new Blob([JSON.stringify(geoJsonToExport, null, 2)], {
      type: "application/json",
    });
    FileSaver.saveAs(blob, "lines.json");
    commit(types.SET_UNDO_STACK, []);
    commit(types.SET_REDO_STACK, []);
  },
  saveAsGeoJsonPoints: async ({ getters, state, commit }, { applyFilters = true } = {}) => {
    const geoJsonToExport = applyFilters
      ? buildFilteredGeoJson(getters.getGeoJson, (feature) =>
          isPointFeature(feature?.geometry?.type)
        )
      : buildUnfilteredGeoJson(state, (feature) =>
          isPointFeature(feature?.geometry?.type)
        );
    geoJsonToExport.features = (geoJsonToExport.features || []).map((feature) => ({
      type: feature?.type,
      geometry: feature?.geometry,
      properties: feature?.properties || {},
    }));
    const blob = new Blob([JSON.stringify(geoJsonToExport, null, 2)], {
      type: "application/json",
    });
    FileSaver.saveAs(blob, "points.json");
    commit(types.SET_UNDO_STACK, []);
    commit(types.SET_REDO_STACK, []);
  },
  saveAsGeoJsonSubstations: async ({ getters }) => {
    const geoJsonToExport = buildFilteredGeoJson(getters.getGeoJson, (feature) =>
      isPointFeature(feature?.geometry?.type) &&
      getPointCategory(feature) === "substation"
    );
    geoJsonToExport.features =
      await exportFunctions.preprocessFeaturesForExport(geoJsonToExport);
    const blob = new Blob([JSON.stringify(geoJsonToExport, null, 2)], {
      type: "application/json",
    });
    FileSaver.saveAs(blob, "substations.geojson");
  },
  saveAsGeoJsonGenerators: async ({ getters }) => {
    const geoJsonToExport = buildFilteredGeoJson(getters.getGeoJson, (feature) =>
      isPointFeature(feature?.geometry?.type) &&
      getPointCategory(feature) === "generator"
    );
    geoJsonToExport.features =
      await exportFunctions.preprocessFeaturesForExport(geoJsonToExport);
    const blob = new Blob([JSON.stringify(geoJsonToExport, null, 2)], {
      type: "application/json",
    });
    FileSaver.saveAs(blob, "generators.geojson");
  },
  saveAsKML: async ({ getters }) => {
    const geoJsonToExport = getters.getGeoJson;
    geoJsonToExport.features =
      await exportFunctions.preprocessFeaturesForExport(geoJsonToExport);
    const kml = tokml(geoJsonToExport);
    const blob = new Blob([kml], {
      type: "application/vnd.google-earth.kml+xml",
    });
    FileSaver.saveAs(blob, "krata.kml");
  },
  saveAsKMLLines: async ({ getters }) => {
    const geoJsonToExport = buildFilteredGeoJson(getters.getGeoJson, (feature) =>
      isLineFeature(feature?.geometry?.type)
    );
    geoJsonToExport.features = limitLineExportProperties(
      geoJsonToExport.features
    );
    const kml = tokml(geoJsonToExport);
    const blob = new Blob([kml], {
      type: "application/vnd.google-earth.kml+xml",
    });
    FileSaver.saveAs(blob, "lines.kml");
  },
  saveAsKMLSubstations: async ({ getters }) => {
    const geoJsonToExport = buildFilteredGeoJson(getters.getGeoJson, (feature) =>
      isPointFeature(feature?.geometry?.type) &&
      getPointCategory(feature) === "substation"
    );
    geoJsonToExport.features =
      await exportFunctions.preprocessFeaturesForExport(geoJsonToExport);
    const kml = tokml(geoJsonToExport);
    const blob = new Blob([kml], {
      type: "application/vnd.google-earth.kml+xml",
    });
    FileSaver.saveAs(blob, "substations.kml");
  },
  saveAsKMLGenerators: async ({ getters }) => {
    const geoJsonToExport = buildFilteredGeoJson(getters.getGeoJson, (feature) =>
      isPointFeature(feature?.geometry?.type) &&
      getPointCategory(feature) === "generator"
    );
    geoJsonToExport.features =
      await exportFunctions.preprocessFeaturesForExport(geoJsonToExport);
    const kml = tokml(geoJsonToExport);
    const blob = new Blob([kml], {
      type: "application/vnd.google-earth.kml+xml",
    });
    FileSaver.saveAs(blob, "generators.kml");
  },
  loadKMLFile: async ({ state, dispatch, commit }, file) => {
    try {
      if (state.importLocked) return false;
      const text = await fileReader(file);
      // DOM Parser is not available in web workers, so implementing it in here
      const parsedKML = await new DOMParser().parseFromString(text, "text/xml");
      const geoJson = toGeoJSON.kml(parsedKML);
      const features = await workerFunctions.getFeaturesFromKMLFile(geoJson);
      if (features) {
        dispatch("addGeoJsonFeatures", { features, cascadeStyles: false, shouldUpdateBBOX: true, source: "import" });
        commit(types.SET_IMPORT_LOCKED, true);
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  loadGeoJSONFile: async ({ dispatch }, file) => {
    try {
      const features = await workerFunctions.getFeaturesFromGeoJSONFile(file);
      if (features) {
        dispatch("addGeoJsonFeatures", { features, cascadeStyles: false, shouldUpdateBBOX: true, source: "import" });
      }
      return true;
    } catch (errors) {
      console.error(errors);
      return false;
    }
  },
  loadCSVFileMetaData: async (_, file) => {
    try {
      const metaData = await workerFunctions.getCSVFileMetaData(file);
      return metaData;
    } catch (error) {
      return { error: true };
    }
  },
  loadCSVFile: async (
    { state, dispatch, commit },
    { file, latitudeColumn, longitudeColumn, nameColumn }
  ) => {
    try {
      if (state.importLocked) return false;
      const features = await workerFunctions.getFeaturesFromCSVFile(
        file,
        latitudeColumn,
        longitudeColumn,
        nameColumn
      );
      console.log(`Features generated from CSV file`);
      if (features) {
        await dispatch("addGeoJsonFeatures", {
          features,
          cascadeStyles: false,
          shouldUpdateBBOX: true,
          source: "import",
        });
        commit(types.SET_IMPORT_LOCKED, true);
      }
      return true;
    } catch (errors) {
      console.error(errors);
      return false;
    }
  },
  saveAsImage: async ({ commit, getters, rootGetters }) => {
    commit(types.SET_IS_GENERATING_IMAGE, true);
    const project = rootGetters["projects/getActiveProject"];
    const filename =
      project && project.id
        ? `${getters.getFilename
            .split(" ")
            .join("_")}_${new Date().toISOString()}.jpg`
        : "krata-map.jpg";
    const imageBlob = await generateEditorScreenshot({ quality: 1 });
    FileSaver.saveAs(imageBlob, filename);
    commit(types.SET_UNDO_STACK, []);
    commit(types.SET_REDO_STACK, []);
  },
  loadEditorData: async ({ dispatch }, { title, features }) => {
    dispatch("setFilename", title);
    await dispatch("setFeatures", {
      features: features || {},
      featureIds: (features && Object.keys(features).reverse()) || [],
    });
    dispatch("updateBoundingBox");
  },
  reset: async ({ commit }) => {
    commit(types.RESET);
  },
  clearAllFeatures: async ({ state, commit, dispatch }) => {
    if (state.featureIds?.length) {
      pushUndoSnapshot(commit, state);
    }
    commit(types.RESET);
    sync(dispatch, PROJECT_ACTIONS.DELETE_FEATURES, [...state.featureIds]);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURE_ORDER, []);
  },
  undoLastChange: async ({ state, commit, dispatch }) => {
    const undoStack = state.undoStack || [];
    if (!undoStack.length) return false;
    const targetSnapshot = undoStack[undoStack.length - 1];
    const currentSnapshot = cloneSnapshot(state);
    commit(types.SET_FEATURES, {
      features: targetSnapshot.features,
      featureIds: targetSnapshot.featureIds,
    });
    commit(types.SET_UNDO_STACK, undoStack.slice(0, -1));
    commit(types.SET_REDO_STACK, [...(state.redoStack || []), currentSnapshot]);
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, targetSnapshot.features);
    sync(
      dispatch,
      PROJECT_ACTIONS.UPDATE_FEATURE_ORDER,
      targetSnapshot.featureIds
    );
    return true;
  },
  redoLastChange: async ({ state, commit, dispatch }) => {
    const redoStack = state.redoStack || [];
    if (!redoStack.length) return false;
    const targetSnapshot = redoStack[redoStack.length - 1];
    const currentSnapshot = cloneSnapshot(state);
    commit(types.SET_FEATURES, {
      features: targetSnapshot.features,
      featureIds: targetSnapshot.featureIds,
    });
    commit(types.SET_UNDO_STACK, [...(state.undoStack || []), currentSnapshot]);
    commit(types.SET_REDO_STACK, redoStack.slice(0, -1));
    sync(dispatch, PROJECT_ACTIONS.UPDATE_FEATURES, targetSnapshot.features);
    sync(
      dispatch,
      PROJECT_ACTIONS.UPDATE_FEATURE_ORDER,
      targetSnapshot.featureIds
    );
    return true;
  },
};
